import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, eachDayOfInterval, isSameMonth, isSameDay, parseISO, addDays } from 'date-fns'
import { ChevronLeft, ChevronRight, FileText, Clock, CheckCircle, Filter, X } from 'lucide-react'

import { articlesApi, sitesApi } from '@/services/api'
import type { CalendarEvent, Site } from '@/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

type ViewMode = 'month' | 'week'

const eventTypeColors: Record<string, string> = {
  article: 'bg-blue-100 text-blue-800 border-blue-200',
  scheduled: 'bg-amber-100 text-amber-800 border-amber-200',
  published: 'bg-green-100 text-green-800 border-green-200',
}

const eventTypeIcons: Record<string, typeof FileText> = {
  article: FileText,
  scheduled: Clock,
  published: CheckCircle,
}

export function ContentCalendarPage() {
  const { t } = useTranslation()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [selectedSiteId, setSelectedSiteId] = useState<string>('all')
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate)
      const monthEnd = endOfMonth(currentDate)
      // Extend to full weeks for calendar display
      const start = startOfWeek(monthStart, { weekStartsOn: 0 })
      const end = endOfWeek(monthEnd, { weekStartsOn: 0 })
      return { start, end }
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 })
      return { start: weekStart, end: weekEnd }
    }
  }, [currentDate, viewMode])

  // Fetch sites for filter
  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list({ page_size: 100 }),
  })

  // Fetch calendar data
  const { data: calendarData, isLoading } = useQuery({
    queryKey: ['calendar', format(dateRange.start, 'yyyy-MM-dd'), format(dateRange.end, 'yyyy-MM-dd'), selectedSiteId],
    queryFn: () =>
      articlesApi.getCalendar(
        format(dateRange.start, 'yyyy-MM-dd'),
        format(dateRange.end, 'yyyy-MM-dd'),
        selectedSiteId === 'all' ? undefined : selectedSiteId
      ),
  })

  // Group events by date
  const eventsByDate = useMemo((): Record<string, CalendarEvent[]> => {
    if (!calendarData?.events) return {}

    const grouped: Record<string, CalendarEvent[]> = {}
    for (const event of calendarData.events) {
      const dateKey = event.date.split('T')[0] || ''
      if (dateKey) {
        if (!grouped[dateKey]) grouped[dateKey] = []
        grouped[dateKey].push(event)
      }
    }
    return grouped
  }, [calendarData])

  // Generate calendar days
  const calendarDays = useMemo(() => {
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end })
  }, [dateRange])

  const navigatePrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1))
    } else {
      setCurrentDate(addDays(currentDate, -7))
    }
  }

  const navigateNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1))
    } else {
      setCurrentDate(addDays(currentDate, 7))
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  const sites = sitesData?.items || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('calendar.title')}</h1>
          <p className="text-muted-foreground">
            {t('calendar.description')}
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={navigatePrevious}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={goToToday}>
                {t('calendar.today')}
              </Button>
              <Button variant="outline" size="icon" onClick={navigateNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold ml-4">
                {viewMode === 'month'
                  ? format(currentDate, 'MMMM yyyy')
                  : `Week of ${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`}
              </h2>
            </div>

            {/* View Toggle & Filters */}
            <div className="flex items-center gap-4">
              {/* Site Filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('calendar.allSites')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('calendar.allSites')}</SelectItem>
                    {sites.map((site: Site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex rounded-md border">
                <Button
                  variant={viewMode === 'month' ? 'default' : 'ghost'}
                  size="sm"
                  className="rounded-e-none"
                  onClick={() => setViewMode('month')}
                >
                  {t('calendar.month')}
                </Button>
                <Button
                  variant={viewMode === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  className="rounded-s-none"
                  onClick={() => setViewMode('week')}
                >
                  {t('calendar.week')}
                </Button>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <span className="text-sm text-muted-foreground">{t('calendar.legend')}:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" />
              <span className="text-sm">{t('calendar.eventTypes.article')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-amber-100 border border-amber-200" />
              <span className="text-sm">{t('calendar.eventTypes.scheduled')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
              <span className="text-sm">{t('calendar.eventTypes.published')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {calendarData && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <FileText className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{calendarData.total_articles}</p>
                  <p className="text-sm text-muted-foreground">{t('calendar.stats.articles')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Clock className="h-8 w-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold">{calendarData.total_scheduled}</p>
                  <p className="text-sm text-muted-foreground">{t('calendar.stats.scheduled')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{calendarData.total_published}</p>
                  <p className="text-sm text-muted-foreground">{t('calendar.stats.published')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendar Grid */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              {/* Day Headers */}
              <div className="grid grid-cols-7 bg-muted">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="p-2 text-center text-sm font-medium">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day: Date) => {
                  const dateKey = format(day, 'yyyy-MM-dd')
                  const dayEvents = eventsByDate[dateKey] ?? []
                  const isToday = isSameDay(day, new Date())
                  const isCurrentMonth = isSameMonth(day, currentDate)

                  return (
                    <div
                      key={dateKey}
                      className={cn(
                        'min-h-[120px] border-t border-r p-1',
                        !isCurrentMonth && 'bg-muted/30',
                        isToday && 'bg-primary/5'
                      )}
                    >
                      <div
                        className={cn(
                          'text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full',
                          isToday && 'bg-primary text-primary-foreground'
                        )}
                      >
                        {format(day, 'd')}
                      </div>

                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event) => {
                          const Icon = eventTypeIcons[event.type] || FileText
                          return (
                            <button
                              key={event.id}
                              onClick={() => setSelectedEvent(event)}
                              className={cn(
                                'w-full text-left text-xs p-1 rounded border truncate flex items-center gap-1',
                                eventTypeColors[event.type]
                              )}
                              title={event.title}
                            >
                              <Icon className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{event.title}</span>
                            </button>
                          )
                        })}
                        {dayEvents.length > 3 && (
                          <button
                            onClick={() => {
                              // Could open a modal with all events for this day
                              const event = dayEvents[3]
                              if (event) setSelectedEvent(event)
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            +{dayEvents.length - 3} more
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedEvent(null)}>
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <Badge className={eventTypeColors[selectedEvent.type]}>
                  {selectedEvent.type}
                </Badge>
                <CardTitle className="mt-2">{selectedEvent.title}</CardTitle>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedEvent(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('calendar.modal.date')}</dt>
                  <dd>{format(parseISO(selectedEvent.date), 'PPP p')}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('calendar.modal.status')}</dt>
                  <dd>
                    <Badge variant="outline">{selectedEvent.status}</Badge>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('calendar.modal.type')}</dt>
                  <dd className="capitalize">{selectedEvent.type}</dd>
                </div>
              </dl>
              {selectedEvent.type !== 'scheduled' && !selectedEvent.id.startsWith('schedule-') && (
                <div className="mt-4">
                  <Button asChild className="w-full">
                    <a href={`/articles/${selectedEvent.id}`}>{t('calendar.modal.viewArticle')}</a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
